import { apiEndpoints } from "@config/env";
import { getData } from "../../../utils/global";

/** Active filters echoed on the printed sheet's footer. */
export interface CheatSheetPrintFilterSummary {
  years: string[];
  events: string[];
  /** Resolved source names; empty = all sources. */
  sources: string[];
}

export interface CheatSheetPrintParams {
  className: string;
  projectId: string | number;
  /** The selected configuration (config-filter mode). */
  configName: string;
  /** Variant-specific data-source filter (single source vs fleet source_names). */
  appendSourceFilter: (url: string) => string;
  /** The page's year/event contains-filter appender. */
  appendMetadataFilters: (url: string) => string;
  filterSummary: CheatSheetPrintFilterSummary;
  logPrefix: string;
}

const EMPTY_CELL = "--";

export interface PrintColumn {
  /** Key in the /cheat-sheet config-mode row (lowercased). */
  straightKey: string;
  /** Key in the /maneuver-cheat-sheet?section=accelerations row, or null when no red value is printed. */
  accelKey: string | null;
  header: string;
}

export interface PrintColumnGroup {
  title: string;
  cssClass: string;
  columns: PrintColumn[];
}

/**
 * Printed column layout. The TWS row-label column is rendered before the first group and is
 * spanned by the BASICS group header. accelKey: null = no red acceleration value for that column
 * (BSP and JIB CUNNO accel channels exist but are intentionally omitted from the printout).
 */
export const PRINT_COLUMN_GROUPS: PrintColumnGroup[] = [
  {
    title: "BASICS",
    cssClass: "grp-basics",
    columns: [
      { straightKey: "bsp", accelKey: null, header: "BSP [KPH]" },
      { straightKey: "twa", accelKey: "twa_accmax", header: "TWA [DEG]" },
      { straightKey: "heel_n", accelKey: "heel_accmax", header: "HEEL N [DEG]" },
      { straightKey: "pitch", accelKey: "pitch_accmax", header: "PITCH [DEG]" },
    ],
  },
  {
    title: "FLIGHT",
    cssClass: "grp-flight",
    columns: [
      { straightKey: "rud_rake", accelKey: "rud_rake_accmax", header: "RUD RAKE [DEG]" },
      { straightKey: "rud_diff", accelKey: "rud_diff_accmax", header: "RUD DIFF [DEG]" },
      { straightKey: "db_cant", accelKey: "cant_accmax", header: "DB CANT [DEG]" },
      { straightKey: "db_cant_eff", accelKey: "cant_eff_accmax", header: "DB CANT EFF [DEG]" },
      { straightKey: "db_cant_stow", accelKey: null, header: "DB CANT STOW [DEG]" },
    ],
  },
  {
    title: "WING",
    cssClass: "grp-wing",
    columns: [
      { straightKey: "wing_ca1", accelKey: "wing_ca1_accmax", header: "WING CA1 [DEG]" },
      { straightKey: "wing_twist", accelKey: "wing_twist_accmax", header: "WING TWIST [DEG]" },
      { straightKey: "wing_clew", accelKey: "wing_clew_pos_accmax", header: "WING CLEW [MM]" },
    ],
  },
  {
    title: "JIB",
    cssClass: "grp-jib",
    columns: [
      { straightKey: "jib_sht", accelKey: null, header: "JIB SHT [KGF]" },
      { straightKey: "jib_sht_pct", accelKey: "jib_sheet_pct_accmax", header: "JIB SHT [%]" },
      { straightKey: "jib_cunno", accelKey: null, header: "JIB CUNNO [KGF]" },
      { straightKey: "jib_lead", accelKey: "jib_lead_ang_accmax", header: "JIB LEAD [DEG]" },
    ],
  },
];

type CheatSheetRow = Record<string, unknown>;

export interface PrintCell {
  straight: string;
  /** Raw acceleration value (red column). */
  accel: string | null;
  /** Acceleration expressed as a delta from the straight-line value (accel − straight). */
  accelDelta: string | null;
}

export interface PrintRow {
  tws: string;
  cells: PrintCell[];
}

export interface PrintLegBand {
  label: "UW" | "DW";
  rows: PrintRow[];
}

function normalizeRows(raw: unknown[]): CheatSheetRow[] {
  return raw.map((r) => {
    const row = (r && typeof r === "object" ? r : {}) as CheatSheetRow;
    const out: CheatSheetRow = {};
    for (const k of Object.keys(row)) {
      out[k.toLowerCase()] = row[k];
    }
    return out;
  });
}

/** Round numbers to 1 decimal (same rule as the page tables); strings like "MAX" pass through. */
function formatPrintValue(value: unknown): string {
  if (value == null || value === "") {return EMPTY_CELL;}
  if (typeof value === "number") {
    if (!Number.isFinite(value)) {return EMPTY_CELL;}
    const rounded = Math.round(value * 10) / 10;
    return rounded === value ? String(value) : rounded.toFixed(1);
  }
  const text = String(value).trim();
  if (text === "") {return EMPTY_CELL;}
  const n = Number(text);
  if (Number.isFinite(n)) {return formatPrintValue(n);}
  return text;
}

/** Parse a raw cell value to a finite number, or null when it isn't numeric (e.g. "MAX"). */
function toNumber(value: unknown): number | null {
  if (value == null || value === "") {return null;}
  const n = typeof value === "number" ? value : Number(String(value).trim());
  return Number.isFinite(n) ? n : null;
}

/** Normalize a row's tws so 30 / "30" / 30.0 match across the two endpoints. */
function twsKey(row: CheatSheetRow): string | null {
  const raw = row.tws;
  if (raw == null || String(raw).trim() === "") {return null;}
  const n = Number(raw);
  return Number.isFinite(n) ? String(n) : String(raw).trim();
}

function indexRowsByTws(rows: CheatSheetRow[]): Map<string, CheatSheetRow> {
  const map = new Map<string, CheatSheetRow>();
  for (const row of rows) {
    const key = twsKey(row);
    if (key != null && !map.has(key)) {map.set(key, row);}
  }
  return map;
}

/** Straight-line averages below this sample count are too thin to publish: they print "--". */
const MIN_STRAIGHT_RECORD_COUNT = 5;

function straightCountOk(row: CheatSheetRow | undefined): boolean {
  if (!row) {return false;}
  const n = Number(row.record_count);
  return Number.isFinite(n) && n >= MIN_STRAIGHT_RECORD_COUNT;
}

function buildLegBand(
  label: "UW" | "DW",
  straightRows: CheatSheetRow[],
  accelRows: CheatSheetRow[]
): PrintLegBand {
  const straightByTws = indexRowsByTws(straightRows);
  const accelByTws = indexRowsByTws(accelRows);
  const keys = [...new Set([...straightByTws.keys(), ...accelByTws.keys()])].sort((a, b) => {
    const na = Number(a);
    const nb = Number(b);
    const aNum = Number.isFinite(na);
    const bNum = Number.isFinite(nb);
    if (aNum && bNum) {return na - nb;}
    if (aNum) {return -1;}
    if (bNum) {return 1;}
    return a.localeCompare(b);
  });

  const columns = PRINT_COLUMN_GROUPS.flatMap((g) => g.columns);
  // The two sides are independent: a thin (or absent) straight-line row hides only the straight-line
  // values, the red accelerations still print. A TWS band is dropped only when neither side has
  // anything to show, so the endpoint's always-present empty bins don't become rows of "--".
  const rows: PrintRow[] = [];
  for (const key of keys) {
    const straight = straightByTws.get(key);
    const accel = accelByTws.get(key);
    const countOk = straightCountOk(straight);
    const cells: PrintCell[] = columns.map((col) => {
      const straightRaw = countOk ? straight?.[col.straightKey] : undefined;
      const straightVal = countOk ? formatPrintValue(straightRaw) : EMPTY_CELL;
      let accelVal: string | null = null;
      let accelDelta: string | null = null;
      if (col.accelKey != null && accel != null) {
        const accelRaw = accel[col.accelKey];
        const formatted = formatPrintValue(accelRaw);
        if (formatted !== EMPTY_CELL) {
          accelVal = formatted;
          const straightNum = toNumber(straightRaw);
          const accelNum = toNumber(accelRaw);
          // Delta = acceleration − straight-line. With no printable straight-line value to subtract
          // from (missing, too few samples, or non-numeric like "MAX"), show the raw acceleration.
          accelDelta =
            straightNum != null && accelNum != null
              ? formatPrintValue(accelNum - straightNum)
              : formatted;
        }
      }
      return { straight: straightVal, accel: accelVal, accelDelta };
    });
    const hasAnyValue = cells.some((c) => c.straight !== EMPTY_CELL || c.accel != null);
    if (hasAnyValue) {rows.push({ tws: key, cells });}
  }

  return { label, rows };
}

async function fetchRows(url: string): Promise<{ rows: CheatSheetRow[]; message?: string }> {
  const result = await getData(url);
  if (!result?.success) {
    return { rows: [], message: result?.message ?? "Request failed" };
  }
  const payload = result.data as { rows?: unknown[] };
  const raw = Array.isArray(payload?.rows) ? payload.rows : [];
  return { rows: normalizeRows(raw) };
}

function formatPrintDate(date: Date): string {
  const pad = (n: number) => String(n).padStart(2, "0");
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())} ${pad(date.getHours())}:${pad(date.getMinutes())}`;
}

export function buildFooterSummary(configName: string, summary: CheatSheetPrintFilterSummary): string {
  const parts = [
    `Config: ${configName}`,
    `Year: ${summary.years.length > 0 ? summary.years.join(", ") : "ALL"}`,
    `Event: ${summary.events.length > 0 ? summary.events.join(", ") : "ALL"}`,
    `Sources: ${summary.sources.length > 0 ? summary.sources.join(", ") : "All"}`,
    `Printed ${formatPrintDate(new Date())}`,
  ];
  return parts.join(" · ");
}

export interface CheatSheetBandsResult {
  /** Present when both straight-line fetches succeeded. */
  bands?: PrintLegBand[];
  /** Non-fatal issues (e.g. accelerations unavailable for a leg). */
  warnings: string[];
  /** Set on hard failure (a straight-line fetch failed). */
  message?: string;
}

/** Fetch and shape the UW/DW band data shared by the print sheet and the Excel export. */
export async function fetchCheatSheetBands(
  params: Pick<
    CheatSheetPrintParams,
    "className" | "projectId" | "configName" | "appendSourceFilter" | "appendMetadataFilters"
  >
): Promise<CheatSheetBandsResult> {
  const { className, projectId, configName, appendSourceFilter, appendMetadataFilters } = params;
  const base = `class_name=${encodeURIComponent(className)}&project_id=${encodeURIComponent(projectId)}&group_by=config&config=${encodeURIComponent(configName)}`;
  const withFilters = (url: string) => appendMetadataFilters(appendSourceFilter(url));
  const straightUrl = (leg: "upwind" | "downwind") =>
    withFilters(`${apiEndpoints.app.data}/cheat-sheet?${base}&leg_type=${leg}`);
  const accelUrl = (maneuver: "tack" | "gybe") =>
    withFilters(`${apiEndpoints.app.data}/maneuver-cheat-sheet?${base}&section=accelerations&maneuver_type=${maneuver}`);

  const [straightUw, straightDw, accelUw, accelDw] = await Promise.all([
    fetchRows(straightUrl("upwind")),
    fetchRows(straightUrl("downwind")),
    fetchRows(accelUrl("tack")).catch((err) => ({ rows: [], message: String(err) })),
    fetchRows(accelUrl("gybe")).catch((err) => ({ rows: [], message: String(err) })),
  ]);

  if (straightUw.message != null || straightDw.message != null) {
    return {
      warnings: [],
      message: `Failed to load cheat sheet data: ${straightUw.message ?? straightDw.message}`,
    };
  }

  const warnings: string[] = [];
  if (accelUw.message != null) {
    warnings.push(`Accelerations unavailable for UW: ${accelUw.message}`);
  }
  if (accelDw.message != null) {
    warnings.push(`Accelerations unavailable for DW: ${accelDw.message}`);
  }

  return {
    bands: [
      buildLegBand("UW", straightUw.rows, accelUw.rows),
      buildLegBand("DW", straightDw.rows, accelDw.rows),
    ],
    warnings,
  };
}
